/**
 * Family Activity Widget
 *
 * Displays recent family activity feed showing who did what.
 * Combines events from calendar, todo lists, and albums.
 *
 * @module shared/components/widgets/FamilyActivityWidget
 */

import { formatDistanceToNow, parseISO } from 'date-fns';
import { bg } from 'date-fns/locale';
import {
  Activity,
  Calendar,
  CheckSquare,
  Image,
  Clock,
  User,
} from 'lucide-react';
import { useMemo } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/shared/components/ui/card';
import { TruncatedText } from '@/shared/components/ui/truncated-text';
import { useAppNavigation } from '@/shared/hooks/useAppNavigation';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';

// ============================================
// Types
// ============================================

/** Minimal event shape used to build the activity feed. */
interface ActivitySourceEvent {
  readonly id: string;
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly createdBy: string;
  readonly emoji?: string | null;
}

/** Minimal family member shape used for activity attribution. */
interface ActivityMember {
  readonly id: string;
  readonly displayName: string;
  readonly avatar?: string | undefined;
}

/** Minimal task shape used to build the activity feed. */
interface ActivitySourceTask {
  readonly id: string;
  readonly title: string;
  readonly status: string;
  readonly createdAt: string;
  readonly completedAt?: string | null;
  readonly assignees?: readonly string[];
  readonly emoji?: string | null;
}

type ActivityType =
  | 'event_created'
  | 'event_updated'
  | 'task_completed'
  | 'task_created'
  | 'album_created'
  | 'photo_added';

interface ActivityItem {
  readonly id: string;
  readonly type: ActivityType;
  readonly title: string;
  readonly timestamp: string;
  readonly memberId: string;
  readonly memberName: string;
  readonly memberAvatar: string | undefined;
  readonly emoji: string | undefined;
  readonly metadata?: Readonly<Record<string, unknown>>;
}

interface FamilyActivityWidgetProps {
  /** Events used to build the activity feed */
  readonly events?: readonly ActivitySourceEvent[];
  /** Family members used for activity attribution */
  readonly members?: readonly ActivityMember[];
  /** Tasks used to build the activity feed */
  readonly tasks?: readonly ActivitySourceTask[];
  /** Number of activities to display */
  readonly count?: number;
  /** Additional CSS classes */
  readonly className?: string;
  /** Show header */
  readonly showHeader?: boolean;
}

// ============================================
// Activity Type Config
// ============================================

const ACTIVITY_CONFIG: Record<
  ActivityType,
  {
    icon: typeof Calendar;
    verb: string;
    color: string;
  }
> = {
  event_created: {
    icon: Calendar,
    verb: 'създаде събитие',
    color: 'text-blue-500',
  },
  event_updated: {
    icon: Calendar,
    verb: 'промени събитие',
    color: 'text-yellow-500',
  },
  task_completed: {
    icon: CheckSquare,
    verb: 'завърши задача',
    color: 'text-green-500',
  },
  task_created: {
    icon: CheckSquare,
    verb: 'създаде задача',
    color: 'text-purple-500',
  },
  album_created: {
    icon: Image,
    verb: 'създаде албум',
    color: 'text-pink-500',
  },
  photo_added: {
    icon: Image,
    verb: 'добави снимка',
    color: 'text-pink-400',
  },
};

// ============================================
// Activity Item Component
// ============================================

interface ActivityItemViewProps {
  readonly activity: ActivityItem;
  readonly onClick?: (() => void) | undefined;
}

function ActivityItemView({ activity, onClick }: ActivityItemViewProps) {
  const config = ACTIVITY_CONFIG[activity.type];
  const Icon = config.icon;

  const relativeTime = useMemo(() => {
    return formatDistanceToNow(parseISO(activity.timestamp), {
      addSuffix: true,
      locale: bg,
    });
  }, [activity.timestamp]);

  // Get initials from name
  const initials = activity.memberName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className={cn(
        'flex items-start gap-3 py-2',
        onClick && 'cursor-pointer rounded transition-colors hover:bg-accent/50'
      )}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      role={onClick ? 'button' : undefined}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tabIndex only set when interactive (onClick present)
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Avatar */}
      <Avatar className="size-8 flex-shrink-0">
        <AvatarImage src={activity.memberAvatar} alt={activity.memberName} />
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          <span className="font-medium">{activity.memberName}</span>{' '}
          <span className="text-muted-foreground">{config.verb}</span>
        </p>

        <div className="mt-0.5 flex items-center gap-1.5">
          <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', config.color)} />
          {activity.emoji && (
            <span className="text-sm" aria-hidden="true">
              {activity.emoji}
            </span>
          )}
          <TruncatedText className="text-sm text-muted-foreground">
            {activity.title}
          </TruncatedText>
        </div>
      </div>

      {/* Timestamp */}
      <span className="flex flex-shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <Clock className="size-3" />
        {relativeTime}
      </span>
    </div>
  );
}

// ============================================
// Main Widget Component
// ============================================

export function FamilyActivityWidget({
  events = [],
  members = [],
  tasks = [],
  count = 5,
  className,
  showHeader = true,
}: FamilyActivityWidgetProps) {
  const { goToCalendar } = useAppNavigation();

  // Create member lookup
  const memberMap = useMemo(() => {
    const map = new Map<string, { name: string; avatar: string | undefined }>();
    for (const member of members) {
      map.set(member.id, {
        name: member.displayName,
        avatar: member.avatar,
      });
    }
    return map;
  }, [members]);

  // Build activity feed from events and tasks
  const activities = useMemo(() => {
    const items: ActivityItem[] = [];

    // Add event activities (using createdAt as activity time)
    for (const event of events) {
      const member = memberMap.get(event.createdBy);
      if (!member) continue;

      items.push({
        id: `event-${event.id}`,
        type: 'event_created',
        title: event.title,
        timestamp: event.createdAt,
        memberId: event.createdBy,
        memberName: member.name,
        memberAvatar: member.avatar,
        emoji: event.emoji ?? undefined,
      });

      // If updated after created, add update activity
      if (event.updatedAt !== event.createdAt) {
        items.push({
          id: `event-update-${event.id}`,
          type: 'event_updated',
          title: event.title,
          timestamp: event.updatedAt,
          memberId: event.createdBy,
          memberName: member.name,
          memberAvatar: member.avatar,
          emoji: event.emoji ?? undefined,
        });
      }
    }

    // Add task activities
    for (const task of tasks) {
      // Use first assignee or creator
      const assigneeId = task.assignees?.[0] ?? 'member-1';
      const member = memberMap.get(assigneeId) ?? {
        name: 'Член на семейството',
        avatar: undefined,
      };

      items.push({
        id: `task-${task.id}`,
        type: task.status === 'completed' ? 'task_completed' : 'task_created',
        title: task.title,
        timestamp:
          task.status === 'completed' && task.completedAt
            ? task.completedAt
            : task.createdAt,
        memberId: assigneeId,
        memberName: member.name,
        memberAvatar: member.avatar,
        emoji: task.emoji ?? undefined,
      });
    }

    // Sort by timestamp descending (most recent first)
    items.sort(
      (a, b) =>
        parseISO(b.timestamp).getTime() - parseISO(a.timestamp).getTime()
    );

    return items.slice(0, count);
  }, [events, tasks, memberMap, count]);

  // Navigate to activity source
  const handleViewAll = () => {
    // Navigate to calendar and open the activity tab in settings
    goToCalendar({ tab: 'activity' });
  };

  // Navigate to a specific event
  const handleActivityClick = (activity: ActivityItem) => {
    // Only navigate for event-related activities
    if (
      activity.type === 'event_created' ||
      activity.type === 'event_updated'
    ) {
      // Extract event ID from activity ID (e.g., 'event-123' → '123')
      const eventId = activity.id.replace(/^event(-update)?-/, '');
      // Extract date from timestamp for navigation (always present)
      const date = activity.timestamp.split('T')[0] ?? '';
      if (date) {
        goToCalendar({ date, highlightEvent: eventId });
      }
    }
    // TODO: Add navigation for tasks and albums when those features support it
  };

  return (
    <Card className={cn('overflow-hidden', className)}>
      {showHeader && (
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-1.5 text-sm">
              <Activity className="size-4" />
              Семейна активност
            </CardTitle>
            <Button
              variant={ButtonVariant.Ghost}
              size={ButtonSize.Small}
              className="h-7 px-2 text-xs"
              onClick={handleViewAll}
            >
              Виж повече
            </Button>
          </div>
        </CardHeader>
      )}

      <CardContent className={cn('p-4', showHeader && 'pt-0')}>
        {activities.length > 0 ? (
          <div className="divide-y divide-border">
            {activities.map((activity) => (
              <ActivityItemView
                key={activity.id}
                activity={activity}
                onClick={
                  activity.type === 'event_created' ||
                  activity.type === 'event_updated'
                    ? () => handleActivityClick(activity)
                    : undefined
                }
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <User className="mb-2 size-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Няма скорошна активност
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Default export for easier imports
export default FamilyActivityWidget;
